import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import request from '../../utils/request';
import PageLayout from '../layout/PageLayout';
import DashboardHeader, { DEFAULT_FILTERS } from './DashboardHeader';
import KpiBar from './KpiBar';
import DashboardCanvas, { getViewMeta } from './DashboardCanvas';
import ContextPanel from './ContextPanel';
import RecordsSheet from './RecordsSheet';
import DashboardDrawer from './DashboardDrawer';
import { CardSkeleton, ErrorState } from './states';
import { formatRangeLabel, resolveRange } from './dateRange';
import { PMS_LABELS, fmtMoney, fmtNumber } from './derive';
import { pctChange } from './coverage';

// Dashboard layout:
//   header (range + state + per-tab filters)
//   KPI bar (5 compact tiles)
//   canvas (tabs: Map / Orders / Hosts / Properties / Drivers) | context panel
// Data: /admin/dashboard/summary for everything date-scoped,
//       /admin/dashboard/coverage for the driver heat map (24h cached server-side).

const DEFAULT_RECORD_STATE = {
  page: 0,
  sort: { key: 'createdAt', dir: 'desc' },
  rows: [],
  totalRows: 0,
  loading: false,
  error: null,
};

const VIEW_TITLES = {
  hosts: 'Host records',
  drivers: 'Driver records',
  orders: 'Order records',
  properties: 'Property records',
};

const CANVAS_HEIGHT = { height: 'calc(100vh - 252px)', minHeight: 560 };

const getDefaultSortForView = (view) => {
  switch (view) {
    case 'hosts':
      return { key: 'orderCount', dir: 'desc' };
    case 'orders':
      return { key: 'inStockDate', dir: 'desc' };
    default:
      return { key: 'createdAt', dir: 'desc' };
  }
};

const buildDashboardParams = (filters, range, overrides = {}) => {
  const params = new URLSearchParams();
  params.set('startDate', range.start.toISOString());
  params.set('endDate', range.end.toISOString());
  const values = {
    pms: filters.pms,
    hostLifecycle: filters.hostLifecycle,
    state: filters.state,
    search: filters.search,
    orderStatus: filters.orderStatus,
    deliveryFeeType: filters.deliveryFeeType,
    storeLocation: filters.storeLocation,
    ...overrides,
  };
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '' && value !== 'all') params.set(key, value);
  });
  return params;
};

const buildSeriesLabels = (rows = []) =>
  rows.map((row) => ({
    ...row,
    label: row.key ? new Date(`${row.key}T00:00:00.000Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '',
  }));

const fmtDate = (value) =>
  value ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

function buildHostColumns() {
  return [
    {
      key: 'name',
      header: 'Host',
      searchable: true,
      sortable: true,
      accessor: (row) => row.name,
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-zinc-900">{row.name}</p>
          <p className="truncate text-[10px] text-zinc-500">{row.email || '—'}</p>
        </div>
      ),
    },
    { key: 'pmsType', header: 'PMS', sortable: true, accessor: (row) => PMS_LABELS[row.pmsType] || '—' },
    { key: 'propertyCount', header: 'Props', align: 'right', sortable: true, accessor: (row) => row.propertyCount || 0 },
    { key: 'orderCount', header: 'Orders', align: 'right', sortable: true, accessor: (row) => row.orderCount || 0 },
    { key: 'aov', header: 'AOV', align: 'right', sortable: true, accessor: (row) => row.aov || 0, cell: (row) => fmtMoney(row.aov || 0) },
    { key: 'revenue', header: 'Volume', align: 'right', sortable: true, accessor: (row) => row.revenue || 0, cell: (row) => fmtMoney(row.revenue || 0) },
    {
      key: 'isNew',
      header: 'Lifecycle',
      sortable: true,
      accessor: (row) => (row.isNew ? 'New' : 'Legacy'),
      cell: (row) => (
        <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${row.isNew ? 'bg-teal-50 text-emerald-700' : 'bg-zinc-100 text-zinc-600'}`}>
          {row.isNew ? 'New' : 'Legacy'}
        </span>
      ),
    },
  ];
}

function buildOrderColumns() {
  return [
    {
      key: 'uniqueId',
      header: 'Order',
      searchable: true,
      sortable: true,
      accessor: (row) => row.uniqueId || row._id,
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-zinc-900">{row.uniqueId || String(row._id).slice(-6)}</p>
          <p className="truncate text-[10px] text-zinc-500">{row?.property?.address?.line1 || '—'}</p>
        </div>
      ),
    },
    { key: 'inStockDate', header: 'In stock', sortable: true, sortAccessor: (row) => new Date(row.inStockDate || 0).getTime(), cell: (row) => fmtDate(row.inStockDate) },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      searchable: true,
      cell: (row) => <span className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-zinc-700">{row.status}</span>,
    },
    { key: 'storeLocation', header: 'Store', sortable: true, searchable: true, accessor: (row) => row.storeLocation || '—' },
    { key: 'revenue', header: 'Revenue', align: 'right', sortable: true, accessor: (row) => row._revenue || 0, cell: (row) => fmtMoney(row._revenue || 0) },
  ];
}

function buildDriverColumns() {
  return [
    {
      key: 'name',
      header: 'Driver',
      searchable: true,
      sortable: true,
      accessor: (row) => row.name,
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-zinc-900">{row.name}</p>
          <p className="truncate text-[10px] text-zinc-500">{row.email || '—'}</p>
        </div>
      ),
    },
    { key: 'createdAt', header: 'Signed up', sortable: true, sortAccessor: (row) => new Date(row.createdAt || 0).getTime(), cell: (row) => fmtDate(row.createdAt) },
    {
      key: 'market',
      header: 'City / State',
      sortable: true,
      searchable: true,
      accessor: (row) => `${row.market || '—'}${row?.address?.state ? `, ${row.address.state}` : ''}`,
    },
    {
      key: 'active',
      header: 'Status',
      sortable: true,
      accessor: (row) => (row.active ? 'Active' : 'Inactive'),
      cell: (row) => (
        <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${row.active ? 'bg-teal-50 text-emerald-700' : 'bg-orange-50 text-orange-700'}`}>
          {row.active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ];
}

function buildPropertyColumns() {
  return [
    {
      key: 'address',
      header: 'Address',
      searchable: true,
      sortable: true,
      accessor: (row) =>
        [
          row?.address?.line1,
          row?.address?.line2,
          row?.address?.city,
          row?.address?.state,
          row?.address?.zipCode,
          row?.listingTitle,
          row?.uniqueId,
        ]
          .filter(Boolean)
          .join(' '),
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-zinc-900">{row?.address?.line1 || row?.listingTitle || 'Property'}</p>
          <p className="truncate text-[10px] text-zinc-500">
            {[row?.address?.city, row?.address?.state, row?.address?.zipCode].filter(Boolean).join(', ') || '—'}
          </p>
        </div>
      ),
    },
    {
      key: 'hostName',
      header: 'Host',
      searchable: true,
      sortable: true,
      accessor: (row) => [row.hostName, row.hostEmail, row.hostCompanyName, row.hostPhoneNumber].filter(Boolean).join(' '),
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-zinc-900">{row.hostName || '—'}</p>
          <p className="truncate text-[10px] text-zinc-500">{row.hostEmail || row.hostCompanyName || '—'}</p>
        </div>
      ),
    },
    {
      key: 'active',
      header: 'Host status',
      sortable: true,
      accessor: (row) => (row.active ? 'Active' : 'Inactive'),
      cell: (row) => (
        <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${row.active ? 'bg-teal-50 text-emerald-700' : 'bg-orange-50 text-orange-700'}`}>
          {row.active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    { key: 'createdAt', header: 'Created', sortable: true, sortAccessor: (row) => new Date(row.createdAt || 0).getTime(), cell: (row) => fmtDate(row.createdAt) },
    { key: 'propertySource', header: 'Source', sortable: true, searchable: true, accessor: (row) => PMS_LABELS[row.propertySource] || row.propertySource || '—' },
  ];
}

const RECORD_CONFIGS = {
  hosts: { columns: buildHostColumns, placeholder: 'Search hosts by name, email, company…' },
  drivers: { columns: buildDriverColumns, placeholder: 'Search drivers by name, email, city…' },
  orders: { columns: buildOrderColumns, placeholder: 'Search orders by ID, store, property, host…' },
  properties: { columns: buildPropertyColumns, placeholder: 'Search all properties by address, city, ZIP, host…' },
};

export default function DashboardOverview() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(true);
  const [coverageError, setCoverageError] = useState(null);
  const [activeView, setActiveView] = useState('map');
  const [mapState, setMapState] = useState({ layer: 'drivers', metric: 'drivers' });
  const [highlightId, setHighlightId] = useState(null);
  const [drawer, setDrawer] = useState({ kind: null, payload: null });
  const [recordsView, setRecordsView] = useState(null);
  const [recordsState, setRecordsState] = useState(DEFAULT_RECORD_STATE);
  const summaryRequestRef = useRef(0);
  const coverageRequestRef = useRef(0);
  const recordsRequestRef = useRef(0);

  const range = useMemo(
    () => resolveRange(filters.range, filters.customStart, filters.customEnd),
    [filters.range, filters.customStart, filters.customEnd]
  );
  const rangeLabel = useMemo(
    () => formatRangeLabel(filters.range, filters.customStart, filters.customEnd),
    [filters.range, filters.customStart, filters.customEnd]
  );
  const focusState = filters.state && filters.state !== 'all' ? filters.state : null;

  const loadSummary = useCallback(async () => {
    const requestId = summaryRequestRef.current + 1;
    summaryRequestRef.current = requestId;
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const params = buildDashboardParams(filters, range);
      const { data } = await request.get(`/admin/dashboard/summary?${params.toString()}`);
      if (summaryRequestRef.current !== requestId) return;
      setSummary({
        ...data,
        series: {
          hosts: buildSeriesLabels(data?.series?.hosts || []),
          properties: buildSeriesLabels(data?.series?.properties || []),
          drivers: buildSeriesLabels(data?.series?.drivers || []),
          orders: buildSeriesLabels(data?.series?.orders || []),
        },
      });
    } catch (error) {
      if (summaryRequestRef.current !== requestId) return;
      setSummaryError(error?.response?.data?.error || 'Failed to load dashboard summary');
      setSummary(null);
    } finally {
      if (summaryRequestRef.current === requestId) setSummaryLoading(false);
    }
  }, [filters, range]);

  // Coverage is country-wide; state focus and search are client-side, so
  // only the range and demand-side filters should trigger a reload.
  const coverageQuery = useMemo(
    () =>
      buildDashboardParams(
        {
          pms: filters.pms,
          hostLifecycle: filters.hostLifecycle,
          orderStatus: filters.orderStatus,
          deliveryFeeType: filters.deliveryFeeType,
          storeLocation: filters.storeLocation,
        },
        range
      ).toString(),
    [filters.pms, filters.hostLifecycle, filters.orderStatus, filters.deliveryFeeType, filters.storeLocation, range]
  );

  const loadCoverage = useCallback(
    async ({ refresh = false } = {}) => {
      const requestId = coverageRequestRef.current + 1;
      coverageRequestRef.current = requestId;
      setCoverageLoading(true);
      setCoverageError(null);
      try {
        const params = new URLSearchParams(coverageQuery);
        if (refresh) params.set('refresh', '1');
        const { data } = await request.get(`/admin/dashboard/coverage?${params.toString()}`);
        if (coverageRequestRef.current !== requestId) return;
        setCoverage(data);
      } catch (error) {
        if (coverageRequestRef.current !== requestId) return;
        setCoverageError(error?.response?.data?.error || 'Failed to load driver coverage');
      } finally {
        if (coverageRequestRef.current === requestId) setCoverageLoading(false);
      }
    },
    [coverageQuery]
  );

  const loadRecords = useCallback(async () => {
    if (!recordsView) return;
    const requestId = recordsRequestRef.current + 1;
    recordsRequestRef.current = requestId;
    setRecordsState((current) => ({ ...current, loading: true, error: null }));
    try {
      const params = buildDashboardParams(filters, range, {
        entity: recordsView,
        page: String((recordsState.page || 0) + 1),
        pageSize: '12',
        sortKey: recordsState.sort?.key || '',
        sortDir: recordsState.sort?.dir || 'desc',
      });
      const { data } = await request.get(`/admin/dashboard/records?${params.toString()}`);
      if (recordsRequestRef.current !== requestId) return;
      setRecordsState((current) => ({ ...current, rows: data?.rows || [], totalRows: data?.totalRows || 0, loading: false }));
    } catch (error) {
      if (recordsRequestRef.current !== requestId) return;
      setRecordsState((current) => ({
        ...current,
        rows: [],
        totalRows: 0,
        loading: false,
        error: error?.response?.data?.error || 'Failed to load records',
      }));
    }
  }, [filters, range, recordsState.page, recordsState.sort, recordsView]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadCoverage();
  }, [loadCoverage]);

  useEffect(() => {
    if (recordsView) loadRecords();
  }, [loadRecords, recordsView]);

  const metrics = useMemo(() => {
    const kpis = summary?.kpis || {};
    const platform = summary?.platform || {};
    const previous = summary?.previous;
    return [
      {
        id: 'orders',
        label: 'Orders',
        value: fmtNumber(kpis.orders || 0),
        delta: previous ? pctChange(kpis.orders, previous.orders) : undefined,
        sublabel: previous ? undefined : 'in range',
        hint: `Orders by in-stock date in ${rangeLabel}${previous ? ', compared with the preceding period of equal length' : ''}`,
        tab: 'orders',
      },
      {
        id: 'revenue',
        label: 'Volume',
        value: fmtMoney(kpis.revenue || 0),
        delta: previous ? pctChange(kpis.revenue, previous.revenue) : undefined,
        sublabel: previous ? undefined : `${fmtMoney(kpis.aov || 0)} AOV`,
        hint: `Gross order volume (fees + tips − discounts) · ${fmtMoney(kpis.aov || 0)} average order`,
        tab: 'orders',
      },
      {
        id: 'activeHosts',
        label: 'Active hosts',
        value: fmtNumber(platform.activeHosts || 0),
        sublabel: `+${fmtNumber(kpis.hosts || 0)} new in range`,
        hint: 'Verified and onboarded hosts, platform-wide (state filter applies)',
        tab: 'hosts',
      },
      {
        id: 'activeProperties',
        label: 'Active properties',
        value: fmtNumber(platform.orderingProperties || 0),
        sublabel: `of ${fmtNumber(platform.totalProperties || 0)} total`,
        hint: 'Properties with at least one order in the selected range',
        tab: 'properties',
      },
      {
        id: 'activeDrivers',
        label: 'Active drivers',
        value: fmtNumber(platform.activeDrivers || 0),
        sublabel: `+${fmtNumber(kpis.drivers || 0)} new in range`,
        hint: 'Drivers able to accept an order right now: active, not suspended, documents approved, Checkr cleared',
        tab: 'drivers',
      },
    ];
  }, [summary, rangeLabel]);

  const activeMetricId = useMemo(
    () => ({ map: 'activeDrivers', drivers: 'activeDrivers', orders: 'orders', hosts: 'activeHosts', properties: 'activeProperties' })[activeView],
    [activeView]
  );

  const onChangeFilters = useCallback((patch) => {
    setFilters((current) => ({ ...current, ...patch }));
    setRecordsState((current) => ({ ...current, page: 0 }));
  }, []);

  const onResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setRecordsState((current) => ({ ...current, page: 0 }));
  }, []);

  const onRefresh = useCallback(() => {
    loadSummary();
    loadCoverage({ refresh: activeView === 'map' });
    if (recordsView) loadRecords();
  }, [activeView, loadCoverage, loadRecords, loadSummary, recordsView]);

  const onFocusState = useCallback((code) => onChangeFilters({ state: code || 'all' }), [onChangeFilters]);
  const onChangeMapState = useCallback((patch) => setMapState((current) => ({ ...current, ...patch })), []);

  const openHostDrawer = useCallback((host) => setDrawer({ kind: 'host', payload: host }), []);
  const openPropertyDrawer = useCallback((property) => setDrawer({ kind: 'property', payload: property }), []);
  const openPmsDrawer = useCallback((pmsId) => setDrawer({ kind: 'pms', payload: pmsId }), []);
  const openOrderDrawer = useCallback((order) => setDrawer({ kind: 'order', payload: order }), []);
  const closeDrawer = useCallback(() => setDrawer({ kind: null, payload: null }), []);

  const openRecords = useCallback((view) => {
    if (!view || !RECORD_CONFIGS[view]) return;
    setRecordsView(view);
    setRecordsState({ ...DEFAULT_RECORD_STATE, sort: getDefaultSortForView(view) });
  }, []);
  const closeRecords = useCallback(() => setRecordsView(null), []);

  const onMetricSelect = useCallback((metric) => {
    if (metric.tab) setActiveView(metric.tab);
  }, []);

  const recordsConfig = useMemo(() => {
    if (!recordsView || !RECORD_CONFIGS[recordsView]) return null;
    const def = RECORD_CONFIGS[recordsView];
    const onRowClick = { hosts: openHostDrawer, orders: openOrderDrawer, properties: openPropertyDrawer }[recordsView];
    return {
      title: VIEW_TITLES[recordsView] || 'Records',
      rows: recordsState.rows || [],
      totalRows: recordsState.totalRows || 0,
      page: recordsState.page || 0,
      onPageChange: (page) => setRecordsState((current) => ({ ...current, page })),
      sort: recordsState.sort,
      onSortChange: (sort) => setRecordsState((current) => ({ ...current, sort, page: 0 })),
      query: filters.search,
      onQueryChange: (search) => onChangeFilters({ search }),
      manualMode: true,
      columns: def.columns(),
      onRowClick,
      searchPlaceholder: def.placeholder,
      initialSort: getDefaultSortForView(recordsView),
    };
  }, [filters.search, onChangeFilters, openHostDrawer, openOrderDrawer, openPropertyDrawer, recordsState, recordsView]);

  const subtitle = useMemo(() => {
    const totals = coverage?.totals;
    const parts = [rangeLabel];
    if (summary?.rowCounts) parts.push(`${fmtNumber(summary.rowCounts.orders || 0)} orders`);
    if (totals) parts.push(`${fmtNumber(totals.activeDrivers)} active drivers`);
    return parts.join(' · ');
  }, [coverage, rangeLabel, summary]);

  const viewMeta = getViewMeta(activeView, summary, coverage);

  if (summaryLoading && !summary && coverageLoading && !coverage) {
    return (
      <PageLayout title="Dashboard" subtitle="Loading…" showSearch={false} fullWidth>
        <div className="space-y-3">
          <CardSkeleton className="h-12" height={48} />
          <div className="grid grid-cols-2 gap-2 laptop:grid-cols-5">
            {[0, 1, 2, 3, 4].map((i) => (
              <CardSkeleton key={i} height={60} />
            ))}
          </div>
          <div className="grid grid-cols-1 gap-3 laptop:grid-cols-12">
            <CardSkeleton className="laptop:col-span-8" height={560} />
            <CardSkeleton className="laptop:col-span-4" height={560} />
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Dashboard" subtitle={subtitle} showSearch={false} fullWidth>
      <div className="space-y-3">
        <DashboardHeader
          activeView={activeView}
          filters={filters}
          onChange={onChangeFilters}
          onReset={onResetFilters}
          onRefresh={onRefresh}
          refreshing={summaryLoading || coverageLoading || recordsState.loading}
          rangeLabel={rangeLabel}
        />

        {summaryError && !summary ? (
          <ErrorState title="Could not load dashboard" description={summaryError} onRetry={onRefresh} />
        ) : (
          <KpiBar metrics={metrics} activeId={activeMetricId} onSelect={onMetricSelect} loading={summaryLoading} />
        )}

        <div className="grid grid-cols-1 gap-3 laptop:grid-cols-12">
          <div className="min-h-0 laptop:col-span-8" style={CANVAS_HEIGHT}>
            <DashboardCanvas
              activeView={activeView}
              onChangeView={setActiveView}
              onOpenRecords={openRecords}
              rangeLabel={rangeLabel}
              summary={summary}
              coverage={coverage}
              coverageLoading={coverageLoading}
              coverageError={coverageError}
              onRefreshCoverage={() => loadCoverage({ refresh: true })}
              mapState={mapState}
              onChangeMapState={onChangeMapState}
              focusState={focusState}
              onFocusState={onFocusState}
              highlightId={highlightId}
              onHighlight={setHighlightId}
              onSelectHost={openHostDrawer}
              onSelectPms={openPmsDrawer}
            />
          </div>
          <div className="min-h-0 laptop:col-span-4" style={CANVAS_HEIGHT}>
            <ContextPanel
              activeView={activeView}
              summary={summary}
              coverage={coverage}
              coverageLoading={coverageLoading}
              focusState={focusState}
              onFocusState={onFocusState}
              highlightId={highlightId}
              onHighlight={setHighlightId}
              onSelectHost={openHostDrawer}
              onSelectProperty={openPropertyDrawer}
              onSelectPms={openPmsDrawer}
              onChangeView={setActiveView}
              onOpenRecords={() => openRecords(viewMeta.recordsView)}
              recordsAvailable={viewMeta.recordsAvailable}
            />
          </div>
        </div>
      </div>

      <DashboardDrawer
        open={Boolean(drawer.kind)}
        kind={drawer.kind}
        payload={drawer.payload}
        onClose={closeDrawer}
        filters={{
          startDate: range.start.toISOString(),
          endDate: range.end.toISOString(),
          pms: filters.pms,
          hostLifecycle: filters.hostLifecycle,
          state: filters.state,
          search: filters.search,
          orderStatus: filters.orderStatus,
          deliveryFeeType: filters.deliveryFeeType,
          storeLocation: filters.storeLocation,
        }}
        hosts={[]}
        onOpenHost={openHostDrawer}
        onOpenProperty={openPropertyDrawer}
      />

      <RecordsSheet open={Boolean(recordsView)} onClose={closeRecords} config={recordsConfig} />
    </PageLayout>
  );
}
